import PaymentIcon from '@assets/svg/icons/cb-payment.svg'
import ConciergeIcon from '@assets/svg/icons/concierge.svg'
import ContactlessIcon from '@assets/svg/icons/contactless.svg'
import PaymentCeilingIcon from '@assets/svg/icons/payment-ceiling.svg'
import RealTimeIcon from '@assets/svg/icons/real-time.svg'
import ServiceIcon from '@assets/svg/icons/service.svg'
import WorldIcon from '@assets/svg/icons/world.svg'
import IconCb from '@fortuneo/ui-icons/general/IconCb'
import IconFlightTakeoff from '@fortuneo/ui-icons/general/IconFlightTakeoff'
import IconUmbrella from '@fortuneo/ui-icons/general/IconUmbrella'
import { useInternalLinkProps } from '@hooks/links/use-internal-link-props'
import { Divider } from '@ui/atoms/Divider'
import { Link } from '@ui/atoms/link'
import { Hyperlink } from '@ui/hyperlink'
import { SuperscriptUI } from '@ui/superscript'
import React, { type ReactNode } from 'react'

import { type CardAdvantageProps as AdvantageCard } from './card/card-advantage'

export enum CARD_FIELDS {
  HEAD = 'general',
  BODY = 'advantages',
}

type CardData = {
  general: {
    name: string
    assetName: string
    underCondition: ReactNode
    link: string
  }
  advantages: Record<string, AdvantageCard['advantage']>
}

export const FOSFO: CardData = {
  general: {
    name: 'Fosfo Mastercard',
    assetName: 'fosfo',
    underCondition: (
      <>
        <strong>Gratuite</strong> <SuperscriptUI value={1} /> à partir d’un
        paiement carte par mois
      </>
    ),
    link: '/compte-bancaire/carte-fosfo-mastercard',
  },
  advantages: {
    price: {
      Icon: IconCb,
      content: (
        <p className="leading-alternate">
          Eligibilité :
          <strong> sans conditions de revenus ou d’épargne </strong>
          pour un compte bancaire individuel ou joint avec une seule carte
        </p>
      ),
    },
    charges: {
      Icon: WorldIcon,
      content: (
        <p className="leading-alternate">
          <strong>Aucuns frais</strong> sur tous vos paiements et retraits par
          carte bancaire,
          <strong> partout dans le monde</strong> <SuperscriptUI value={2} />
        </p>
      ),
    },
    flow: {
      Icon: PaymentIcon,
      content: (
        <p className="leading-alternate">
          A <strong>débit immédiat</strong>
        </p>
      ),
    },
    contactless: {
      Icon: ContactlessIcon,
      content: (
        <p className="leading-alternate">
          Sans Contact et <strong>compatible paiement mobile</strong>
        </p>
      ),
    },
    realtime: {
      Icon: RealTimeIcon,
      content: (
        <p className="leading-alternate">
          Suivis des <strong>opérations cartes en temps réel</strong> depuis
          votre application Fortuneo <SuperscriptUI value={3} />
        </p>
      ),
    },
    ceiling: {
      Icon: PaymentCeilingIcon,
      content: (
        <p className="leading-alternate">
          <strong>Des plafonds</strong> de paiement et de retrait élevés pour
          votre quotidien, vos loisirs et voyages
        </p>
      ),
    },
    insurance: {
      Icon: IconUmbrella,
      content: (
        <>
          <p className="leading-alternate">
            <strong>
              Des garanties d'assurance et d'assistance d'une carte classique CB
              Mastercard
            </strong>{' '}
            pour votre quotidien
          </p>
          <div className="pt-6">
            <Hyperlink href="https://www.fortuneo.fr/files/mastercard-garanties-assistance-assurance.pdf">
              Voir la notice d’assurance et d’assistance
            </Hyperlink>
          </div>
        </>
      ),
    },
    access: {
      Icon: IconFlightTakeoff,
      content: (
        <p className="leading-alternate">
          <strong>Des accès privilégiés pour voyager en toute sérénité</strong>{' '}
          dans les aéroports participants
        </p>
      ),
      disabled: true,
    },
    concierge: {
      Icon: ConciergeIcon,
      content: (
        <p className="leading-alternate">
          <strong>Service de conciergerie</strong> à votre service{' '}
          <strong>24h/24</strong>
        </p>
      ),
      disabled: true,
    },
    service: {
      Icon: ServiceIcon,
      content: (
        <p className="leading-alternate">
          <strong>Services d’exception et des offres privilégiées</strong> en
          France et à l’étranger avec Mastercard
        </p>
      ),
      disabled: true,
    },
  },
}

export const GOLD: CardData = {
  general: {
    name: 'Gold CB Mastercard',
    assetName: 'gold',
    underCondition: FOSFO.general.underCondition,
    link: '/compte-bancaire/carte-gold-mastercard',
  },
  advantages: {
    price: {
      Icon: IconCb,
      content: (
        <p className="leading-alternate">
          Eligibilité : <strong>dès 1 800€ de revenus nets mensuels</strong>{' '}
          pour un compte bancaire individuel ou joint avec une seule carte
        </p>
      ),
      modalTrigger: (open) => (
        <div className="mt-3">
          <Link component="button" variant="legacy" onClick={open}>
            Déjà client Fortuneo
          </Link>
        </div>
      ),
      modalTitle: "En savoir plus sur l'éligibilité",
      modalContent: (
        <>
          <h4 className="mb-4 text-135 leading-alternate">
            Nouveau client <SuperscriptUI value={1} />
          </h4>
          <p className="mb-8 leading-alternate">
            1 800€ de revenus nets mensuels à justifier à la souscription pour
            une seule carte
          </p>
          <h4 className="mb-4 text-135 leading-alternate">
            Déjà client <SuperscriptUI value={2} />
          </h4>
          <p>
            1 800€ de revenus nets mensuels à justifier à la souscription <br />
            <strong>ou</strong>
            <br />
            détenir, à la date de demande de souscription, 10 000€ d’épargne
            chez Fortuneo (+1 500€ de revenus nets mensuels pour une carte à
            débit différé)
          </p>
          <Divider className="my-8" />
          <p className="mb-1 text-085 leading-alternate">
            <span className="text-085 font-medium leading-alternate">(1)</span>{' '}
            Personne physique n’étant pas détentrice, à la date de demande de
            souscription à la carte bancaire choisie, d’un produit ou service
            chez Fortuneo.
          </p>
          <p className="text-085 leading-alternate">
            <span className="text-085 font-medium leading-alternate">(2)</span>{' '}
            Personne physique détentrice, à la date de demande de souscription à
            la carte bancaire choisie, d’un produit ou service chez Fortuneo. En
            cas de changement de gamme/débit, les conditions applicables sont
            celles visées dans la brochure tarifaire Fortuneo.
          </p>
        </>
      ),
    },
    charges: FOSFO.advantages.charges,
    flow: {
      Icon: PaymentIcon,
      content: (
        <p className="leading-alternate">
          A <strong>débit immédiat ou différé</strong> dès 6 mois d'ancienneté
        </p>
      ),
    },
    contactless: FOSFO.advantages.contactless,
    realtime: {
      Icon: RealTimeIcon,
      content: (
        <p className="leading-alternate">
          Suivis des <strong>opérations cartes</strong> depuis votre application
          Fortuneo
        </p>
      ),
    },
    ceiling: FOSFO.advantages.ceiling,
    insurance: {
      Icon: IconUmbrella,
      content: (
        <>
          <p className="leading-alternate">
            <strong>
              Des garanties d’assistance et d’assurance renforcées{' '}
            </strong>
            pour vos loisirs et vos voyages
          </p>
          <div className="pt-6">
            <Hyperlink href="https://www.fortuneo.fr/files/gold-garanties-assistance-assurance.pdf">
              Voir la notice d’assurance et d’assistance
            </Hyperlink>
          </div>
        </>
      ),
    },
    access: FOSFO.advantages.access,
    concierge: FOSFO.advantages.concierge,
    service: FOSFO.advantages.service,
  },
}

export const WORLD_ELITE: CardData = {
  general: {
    name: 'World Elite CB Mastercard',
    assetName: 'worldElite',
    underCondition: (
      <>
        <strong>Gratuite</strong> <SuperscriptUI value={1} /> sous réserve d’un
        versement mensuel de 4 000 € sur le compte Fortuneo associé à la carte
      </>
    ),
    link: '/compte-bancaire/carte-world-elite-mastercard',
  },
  advantages: {
    price: {
      Icon: IconCb,
      content: (
        <p className="leading-alternate">
          Eligibilité : <strong>dès 4 000 € de revenus nets mensuels</strong>{' '}
          pour un compte bancaire individuel ou joint avec une seule carte
        </p>
      ),
    },
    charges: FOSFO.advantages.charges,
    flow: {
      Icon: PaymentIcon,
      content: (
        <p className="leading-alternate">
          A <strong>débit immédiat ou différé</strong>
        </p>
      ),
    },
    contactless: FOSFO.advantages.contactless,
    realtime: GOLD.advantages.realtime,
    ceiling: {
      Icon: PaymentCeilingIcon,
      content: (
        <p className="leading-alternate">
          <strong>Des plafonds</strong> de paiement et de retrait élevés adaptés
          aux <strong>situations exceptionelles</strong>
        </p>
      ),
    },
    insurance: {
      Icon: IconUmbrella,
      content: (
        <>
          <p className="leading-alternate">
            <strong>
              Des garanties d’assurance et d’assistance optimales{' '}
            </strong>
            adaptées à toutes situations
          </p>
          <div className="pt-6">
            <Hyperlink href="https://www.fortuneo.fr/files/world-elite-mastercard-notice-assurance-assistance.pdf">
              Voir la notice d’assurance et d’assistance
            </Hyperlink>
          </div>
        </>
      ),
    },
    access: {
      Icon: IconFlightTakeoff,
      content: (
        <p className="leading-alternate">
          <strong>Des accès privilégiés pour voyager en toute sérénité</strong>{' '}
          dans les aéroports participants{' '}
          <Link
            href="/compte-bancaire/carte-world-elite-mastercard#travel-service"
            variant="legacy"
          >
            Voir détails
          </Link>
        </p>
      ),
    },
    concierge: {
      ...FOSFO.advantages.concierge,
      disabled: false,
    },
    service: {
      ...FOSFO.advantages.service,
      disabled: false,
    },
  },
}

export const FOSFO_SHARED: CardData = {
  general: {
    name: 'Fosfo Mastercard',
    assetName: 'fosfo',
    underCondition: (
      <>
        <strong>Gratuite</strong> <SuperscriptUI value={3} /> à partir d’un
        paiement carte par mois, par carte associée au compte joint
      </>
    ),
    link: '/compte-bancaire/carte-fosfo-mastercard',
  },
  advantages: {
    price: {
      Icon: IconCb,
      content: (
        <p className="leading-alternate">
          Eligibilité :
          <strong> sans conditions de revenus ou d’épargne </strong>
          pour un compte bancaire individuel ou joint avec une seule carte
        </p>
      ),
    },
    charges: {
      Icon: WorldIcon,
      content: (
        <p className="leading-alternate">
          <strong>Aucuns frais</strong> sur tous vos paiements et retraits par
          carte bancaire,
          <strong> partout dans le monde</strong> <SuperscriptUI value={4} />
        </p>
      ),
    },
    flow: {
      Icon: PaymentIcon,
      content: (
        <p className="leading-alternate">
          A <strong>débit immédiat</strong>
        </p>
      ),
    },
    contactless: {
      Icon: ContactlessIcon,
      content: (
        <p className="leading-alternate">
          Sans contact et <strong>compatible paiement mobile</strong>
        </p>
      ),
    },
    realtime: {
      Icon: RealTimeIcon,
      content: (
        <p className="leading-alternate">
          Suivis des <strong>opérations cartes en temps réel</strong> depuis
          votre application Fortuneo <SuperscriptUI value={5} />
        </p>
      ),
    },
    ceiling: {
      Icon: PaymentCeilingIcon,
      content: (
        <p className="leading-alternate">
          <strong>Des plafonds</strong> de paiement et de retrait élevés pour
          votre quotidien, vos loisirs et voyages
        </p>
      ),
    },
    insurance: {
      Icon: IconUmbrella,
      content: (
        <>
          <p className="leading-alternate">
            <strong>
              Des garanties d'assurance et d'assistance d'une carte classique CB
              Mastercard
            </strong>{' '}
            pour votre quotidien
          </p>
          <div className="pt-6">
            <Hyperlink href="https://www.fortuneo.fr/files/mastercard-garanties-assistance-assurance.pdf">
              Voir la notice d’assurance et d’assistance
            </Hyperlink>
          </div>
        </>
      ),
    },
    access: {
      Icon: IconFlightTakeoff,
      content: (
        <p className="leading-alternate">
          <strong>Des accès privilégiés pour voyager en toute sérénité</strong>{' '}
          dans les aéroports participants
        </p>
      ),
      disabled: true,
    },
    concierge: {
      Icon: ConciergeIcon,
      content: (
        <p className="leading-alternate">
          <strong>Service de conciergerie</strong> à votre service{' '}
          <strong>24h/24</strong>
        </p>
      ),
      disabled: true,
    },
    service: {
      Icon: ServiceIcon,
      content: (
        <p className="leading-alternate">
          <strong>Services d’exception et des offres privilégiées</strong> en
          France et à l’étranger avec Mastercard
        </p>
      ),
      disabled: true,
    },
  },
}

export const GOLD_SHARED: CardData = {
  general: {
    name: 'Gold CB Mastercard',
    assetName: 'gold',
    underCondition: FOSFO_SHARED.general.underCondition,
    link: '/compte-bancaire/carte-gold-mastercard',
  },
  advantages: {
    price: {
      Icon: IconCb,
      content: (
        <p className="leading-alternate">
          Eligibilité : <strong>dès 2 700€ de revenus nets mensuels</strong> à
          justifier <strong>à deux</strong> à la souscription
        </p>
      ),
      modalTrigger: (open) => (
        <div className="mt-3">
          <Link component="button" variant="legacy" onClick={open}>
            Déjà client Fortuneo
          </Link>
        </div>
      ),
      modalTitle: "En savoir plus sur l'éligibilité",
      modalContent: (
        <>
          <h4 className="mb-4 text-135 leading-alternate">
            Nouveau client <SuperscriptUI value={6} />
          </h4>
          <p className="mb-8 leading-alternate">
            2 700€ de revenus nets mensuels à justifier à deux à la souscription
          </p>
          <h4 className="mb-4 text-135 leading-alternate">
            Déjà client <SuperscriptUI value={7} />
          </h4>
          <p>
            2 700€ de revenus nets mensuels à justifier à deux à la souscription
            <br />
            <strong>ou</strong>
            <br />
            détenir, à la date de demande de souscription, 10 000€ d’épargne
            chez Fortuneo à deux (+2 500€ de revenus nets mensuels pour une
            carte à débit différé)
          </p>
          <Divider className="my-8 text-[rgba(208,208,208,1)]" />
          <p className="mb-1 text-085 leading-alternate">
            <span className="text-085 font-medium leading-alternate">(6)</span>{' '}
            Personne physique n’étant pas détentrice, à la date de demande de
            souscription à la carte bancaire choisie, d’un produit ou service
            chez Fortuneo.
          </p>
          <p className="text-085 leading-alternate">
            <span className="text-085 font-medium leading-alternate">(7)</span>{' '}
            Personne physique détentrice, à la date de demande de souscription à
            la carte bancaire choisie, d’un produit ou service chez Fortuneo. En
            cas de changement de gamme/débit, les conditions applicables sont
            celles visées dans la brochure tarifaire Fortuneo.
          </p>
        </>
      ),
    },
    charges: FOSFO_SHARED.advantages.charges,
    flow: {
      Icon: PaymentIcon,
      content: (
        <p className="leading-alternate">
          A <strong>débit immédiat ou différé</strong> dès 6 mois d'ancienneté
          d’un des co-titulaires
        </p>
      ),
    },
    contactless: FOSFO_SHARED.advantages.contactless,
    realtime: {
      Icon: RealTimeIcon,
      content: (
        <p className="leading-alternate">
          Suivis des <strong>opérations cartes</strong> depuis votre application
          Fortuneo
        </p>
      ),
    },
    ceiling: FOSFO_SHARED.advantages.ceiling,
    insurance: {
      Icon: IconUmbrella,
      content: (
        <>
          <p className="leading-alternate">
            <strong>
              Des garanties d’assistance et d’assurance renforcées{' '}
            </strong>
            pour vos loisirs et vos voyages
          </p>
          <div className="pt-6">
            <Hyperlink href="https://www.fortuneo.fr/files/gold-garanties-assistance-assurance.pdf">
              Voir la notice d’assurance et d’assistance
            </Hyperlink>
          </div>
        </>
      ),
    },
    access: FOSFO_SHARED.advantages.access,
    concierge: FOSFO_SHARED.advantages.concierge,
    service: FOSFO_SHARED.advantages.service,
  },
}

export const getWorldEliteShared: () => CardData = () => ({
  general: {
    name: 'World Elite CB Mastercard',
    assetName: 'worldElite',
    underCondition: (
      <>
        <strong>Gratuite</strong> <SuperscriptUI value={3} /> sous réserve d’un
        versement mensuel de 4 000 € sur le compte Fortuneo associé à la carte
      </>
    ),
    link: '/compte-bancaire/carte-world-elite-mastercard',
  },
  advantages: {
    price: {
      Icon: IconCb,
      content: (
        <p className="leading-alternate">
          Eligibilité : <strong>dès 5 500 € de revenus nets mensuels</strong> à
          justifier <strong>à deux</strong> à la souscription
        </p>
      ),
    },
    charges: FOSFO_SHARED.advantages.charges,
    flow: {
      Icon: PaymentIcon,
      content: (
        <p className="leading-alternate">
          A <strong>débit immédiat ou différé</strong>
        </p>
      ),
    },
    contactless: FOSFO_SHARED.advantages.contactless,
    realtime: GOLD_SHARED.advantages.realtime,
    ceiling: {
      Icon: PaymentCeilingIcon,
      content: (
        <p className="leading-alternate">
          <strong>Des plafonds</strong> de paiement et de retrait élevés adaptés
          aux <strong>situations exceptionelles</strong>
        </p>
      ),
    },
    insurance: {
      Icon: IconUmbrella,
      content: (
        <>
          <p className="leading-alternate">
            <strong>
              Des garanties d’assurance et d’assistance optimales{' '}
            </strong>
            adaptées à toutes situations
          </p>
          <div className="pt-6">
            <Hyperlink href="https://www.fortuneo.fr/files/world-elite-mastercard-notice-assurance-assistance.pdf">
              Voir la notice d’assurance et d’assistance
            </Hyperlink>
          </div>
        </>
      ),
    },
    access: {
      Icon: IconFlightTakeoff,
      content: (
        <p className="leading-alternate">
          <strong>Des accès privilégiés pour voyager en toute sérénité</strong>{' '}
          dans les aéroports participants{' '}
          <Link
            {...useInternalLinkProps(
              '/compte-bancaire/carte-world-elite-mastercard#travel-service'
            )}
          >
            Voir détails
          </Link>
        </p>
      ),
    },
    concierge: {
      ...FOSFO_SHARED.advantages.concierge,
      disabled: false,
    },
    service: {
      ...FOSFO_SHARED.advantages.service,
      disabled: false,
    },
  },
})
